/**
 * Generato automaticamente.
 * framework-cfg-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  CreaTipoProcedimentoRequest,
  GetProgressivoResponse,
  GetTipiProcedimentoAooParams,
  GetTipiProcedimentoParams,
  TipoProcedimentoDettaglioUfficiAssociatiDTO,
  TipoProcedimentoDTO,
  TipoProcedimentoUfficioDTO,
  TipoProcedimentoUfficioRequest,
  WsRispostaPaginataListTipoProcedimentoDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce tipo procedimento e il dettaglio di tutti gli uffici associati
 * @summary Restituisce tipo procedimento
 */
export const getTipiProcedimentoId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoProcedimentoDettaglioUfficiAssociatiDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-procedimento/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTipiProcedimentoIdQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/tipi-procedimento/${id}`] as const;

export const getGetTipiProcedimentoIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiProcedimentoId>>,
  TError = ErrorType<string>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiProcedimentoId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiProcedimentoId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTipiProcedimentoIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiProcedimentoId>>
  > = ({ signal }) => getTipiProcedimentoId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTipiProcedimentoIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiProcedimentoId>>
>;
export type GetTipiProcedimentoIdQueryError = ErrorType<string>;

/**
 * @summary Restituisce tipo procedimento
 */
export const useGetTipiProcedimentoId = <
  TData = Awaited<ReturnType<typeof getTipiProcedimentoId>>,
  TError = ErrorType<string>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiProcedimentoId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiProcedimentoIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna tipo procedimento
 * @summary Aggiorna tipo procedimento
 */
export const putTipiProcedimentoId = (
  id: string,
  creaTipoProcedimentoRequest: CreaTipoProcedimentoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TipoProcedimentoDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-procedimento/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: creaTipoProcedimentoRequest,
    },
    options,
  );
};

export const getPutTipiProcedimentoIdMutationOptions = <
  TError = ErrorType<string>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiProcedimentoId>>,
    TError,
    { id: string; data: CreaTipoProcedimentoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putTipiProcedimentoId>>,
  TError,
  { id: string; data: CreaTipoProcedimentoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putTipiProcedimentoId>>,
    { id: string; data: CreaTipoProcedimentoRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putTipiProcedimentoId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutTipiProcedimentoIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putTipiProcedimentoId>>
>;
export type PutTipiProcedimentoIdMutationBody = CreaTipoProcedimentoRequest;
export type PutTipiProcedimentoIdMutationError = ErrorType<string>;

/**
 * @summary Aggiorna tipo procedimento
 */
export const usePutTipiProcedimentoId = <
  TError = ErrorType<string>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiProcedimentoId>>,
    TError,
    { id: string; data: CreaTipoProcedimentoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutTipiProcedimentoIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cancellazione tipo procedimento
 * @summary Cancellazione tipo procedimento
 */
export const deleteTipiProcedimentoId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-procedimenti/tipi-procedimento/${id}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteTipiProcedimentoIdMutationOptions = <
  TError = ErrorType<string>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTipiProcedimentoId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteTipiProcedimentoId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteTipiProcedimentoId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteTipiProcedimentoId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTipiProcedimentoIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteTipiProcedimentoId>>
>;

export type DeleteTipiProcedimentoIdMutationError = ErrorType<string>;

/**
 * @summary Cancellazione tipo procedimento
 */
export const useDeleteTipiProcedimentoId = <
  TError = ErrorType<string>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTipiProcedimentoId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteTipiProcedimentoIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Aggiorna e restituisce il codiceProgressivo e se necessario anche codiceAnno di un tipo procedimento
 * @summary Aggiorna e restituisce il progressivo di un tipo procedimento
 */
export const putTipiProcedimentoIdNextProgressivo = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<GetProgressivoResponse>(
    {
      url: `/framework-cfg-procedimenti/tipi-procedimento/${id}/nextProgressivo`,
      method: 'put',
    },
    options,
  );
};

export const getPutTipiProcedimentoIdNextProgressivoMutationOptions = <
  TError = ErrorType<string>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiProcedimentoIdNextProgressivo>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putTipiProcedimentoIdNextProgressivo>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putTipiProcedimentoIdNextProgressivo>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return putTipiProcedimentoIdNextProgressivo(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutTipiProcedimentoIdNextProgressivoMutationResult = NonNullable<
  Awaited<ReturnType<typeof putTipiProcedimentoIdNextProgressivo>>
>;

export type PutTipiProcedimentoIdNextProgressivoMutationError =
  ErrorType<string>;

/**
 * @summary Aggiorna e restituisce il progressivo di un tipo procedimento
 */
export const usePutTipiProcedimentoIdNextProgressivo = <
  TError = ErrorType<string>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiProcedimentoIdNextProgressivo>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutTipiProcedimentoIdNextProgressivoMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Aggiorna e restituisce il codiceProgressivo e se necessario anche codiceAnno di un tipo procedimento ufficio
 * @summary Aggiorna e restituisce il progressivo di un tipo procedimento ufficio
 */
export const putTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioNextProgressivo =
  (
    idTipoProcedimento: string,
    idUfficio: string,
    options?: SecondParameter<typeof apiClientRequest>,
  ) => {
    return apiClientRequest<GetProgressivoResponse>(
      {
        url: `/framework-cfg-procedimenti/tipi-procedimento/${idTipoProcedimento}/ufficio/${idUfficio}/nextProgressivo`,
        method: 'put',
      },
      options,
    );
  };

export const getPutTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioNextProgressivoMutationOptions =
  <TError = ErrorType<string>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof putTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioNextProgressivo
        >
      >,
      TError,
      { idTipoProcedimento: string; idUfficio: string },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<
        typeof putTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioNextProgressivo
      >
    >,
    TError,
    { idTipoProcedimento: string; idUfficio: string },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<
          typeof putTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioNextProgressivo
        >
      >,
      { idTipoProcedimento: string; idUfficio: string }
    > = props => {
      const { idTipoProcedimento, idUfficio } = props ?? {};

      return putTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioNextProgressivo(
        idTipoProcedimento,
        idUfficio,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type PutTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioNextProgressivoMutationResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof putTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioNextProgressivo
      >
    >
  >;

export type PutTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioNextProgressivoMutationError =
  ErrorType<string>;

/**
 * @summary Aggiorna e restituisce il progressivo di un tipo procedimento ufficio
 */
export const usePutTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioNextProgressivo =
  <TError = ErrorType<string>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof putTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioNextProgressivo
        >
      >,
      TError,
      { idTipoProcedimento: string; idUfficio: string },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }) => {
    const mutationOptions =
      getPutTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioNextProgressivoMutationOptions(
        options,
      );

    return useMutation(mutationOptions);
  };
/**
 * Ricerca paginata di tipi procedimento con filtri
 * @summary Ricerca paginata tipi procedimento
 */
export const getTipiProcedimento = (
  params?: GetTipiProcedimentoParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListTipoProcedimentoDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-procedimento`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetTipiProcedimentoQueryKey = (
  params?: GetTipiProcedimentoParams,
) =>
  [
    `/framework-cfg-procedimenti/tipi-procedimento`,
    ...(params ? [params] : []),
  ] as const;

export const getGetTipiProcedimentoQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiProcedimento>>,
  TError = ErrorType<string>,
>(
  params?: GetTipiProcedimentoParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiProcedimento>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiProcedimento>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTipiProcedimentoQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiProcedimento>>
  > = ({ signal }) => getTipiProcedimento(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetTipiProcedimentoQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiProcedimento>>
>;
export type GetTipiProcedimentoQueryError = ErrorType<string>;

/**
 * @summary Ricerca paginata tipi procedimento
 */
export const useGetTipiProcedimento = <
  TData = Awaited<ReturnType<typeof getTipiProcedimento>>,
  TError = ErrorType<string>,
>(
  params?: GetTipiProcedimentoParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiProcedimento>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiProcedimentoQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea tipo procedimento se non esiste
 * @summary Creazione tipo procedimento
 */
export const postTipiProcedimento = (
  creaTipoProcedimentoRequest: CreaTipoProcedimentoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TipoProcedimentoDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-procedimento`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: creaTipoProcedimentoRequest,
    },
    options,
  );
};

export const getPostTipiProcedimentoMutationOptions = <
  TError = ErrorType<string>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipiProcedimento>>,
    TError,
    { data: CreaTipoProcedimentoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postTipiProcedimento>>,
  TError,
  { data: CreaTipoProcedimentoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postTipiProcedimento>>,
    { data: CreaTipoProcedimentoRequest }
  > = props => {
    const { data } = props ?? {};

    return postTipiProcedimento(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTipiProcedimentoMutationResult = NonNullable<
  Awaited<ReturnType<typeof postTipiProcedimento>>
>;
export type PostTipiProcedimentoMutationBody = CreaTipoProcedimentoRequest;
export type PostTipiProcedimentoMutationError = ErrorType<string>;

/**
 * @summary Creazione tipo procedimento
 */
export const usePostTipiProcedimento = <
  TError = ErrorType<string>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipiProcedimento>>,
    TError,
    { data: CreaTipoProcedimentoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostTipiProcedimentoMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Crea un'associazione tra tipo procedimento e ufficio. Se esiste già la elimina e la ricrea con l'abilitazione passata nel body
 * @summary Creazione associazione tra tipo procedimento, ufficio e abilitazione
 */
export const postTipiProcedimentoIdTipoProcedimentoUfficioIdUfficio = (
  idTipoProcedimento: string,
  idUfficio: string,
  tipoProcedimentoUfficioRequest: TipoProcedimentoUfficioRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TipoProcedimentoUfficioDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-procedimento/${idTipoProcedimento}/ufficio/${idUfficio}`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: tipoProcedimentoUfficioRequest,
    },
    options,
  );
};

export const getPostTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioMutationOptions =
  <TError = ErrorType<string>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof postTipiProcedimentoIdTipoProcedimentoUfficioIdUfficio
        >
      >,
      TError,
      {
        idTipoProcedimento: string;
        idUfficio: string;
        data: TipoProcedimentoUfficioRequest;
      },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof postTipiProcedimentoIdTipoProcedimentoUfficioIdUfficio>
    >,
    TError,
    {
      idTipoProcedimento: string;
      idUfficio: string;
      data: TipoProcedimentoUfficioRequest;
    },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<
          typeof postTipiProcedimentoIdTipoProcedimentoUfficioIdUfficio
        >
      >,
      {
        idTipoProcedimento: string;
        idUfficio: string;
        data: TipoProcedimentoUfficioRequest;
      }
    > = props => {
      const { idTipoProcedimento, idUfficio, data } = props ?? {};

      return postTipiProcedimentoIdTipoProcedimentoUfficioIdUfficio(
        idTipoProcedimento,
        idUfficio,
        data,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type PostTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioMutationResult =
  NonNullable<
    Awaited<
      ReturnType<typeof postTipiProcedimentoIdTipoProcedimentoUfficioIdUfficio>
    >
  >;
export type PostTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioMutationBody =
  TipoProcedimentoUfficioRequest;
export type PostTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioMutationError =
  ErrorType<string>;

/**
 * @summary Creazione associazione tra tipo procedimento, ufficio e abilitazione
 */
export const usePostTipiProcedimentoIdTipoProcedimentoUfficioIdUfficio = <
  TError = ErrorType<string>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<typeof postTipiProcedimentoIdTipoProcedimentoUfficioIdUfficio>
    >,
    TError,
    {
      idTipoProcedimento: string;
      idUfficio: string;
      data: TipoProcedimentoUfficioRequest;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioMutationOptions(
      options,
    );

  return useMutation(mutationOptions);
};
/**
 * Cancellazione associazione tra tipo procedimento e ufficio
 * @summary Cancellazione associazione tra tipo procedimento e ufficio
 */
export const deleteTipiProcedimentoIdTipoProcedimentoUfficioIdUfficio = (
  idTipoProcedimento: string,
  idUfficio: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-procedimenti/tipi-procedimento/${idTipoProcedimento}/ufficio/${idUfficio}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioMutationOptions =
  <TError = ErrorType<string>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof deleteTipiProcedimentoIdTipoProcedimentoUfficioIdUfficio
        >
      >,
      TError,
      { idTipoProcedimento: string; idUfficio: string },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<
        typeof deleteTipiProcedimentoIdTipoProcedimentoUfficioIdUfficio
      >
    >,
    TError,
    { idTipoProcedimento: string; idUfficio: string },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<
          typeof deleteTipiProcedimentoIdTipoProcedimentoUfficioIdUfficio
        >
      >,
      { idTipoProcedimento: string; idUfficio: string }
    > = props => {
      const { idTipoProcedimento, idUfficio } = props ?? {};

      return deleteTipiProcedimentoIdTipoProcedimentoUfficioIdUfficio(
        idTipoProcedimento,
        idUfficio,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type DeleteTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioMutationResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof deleteTipiProcedimentoIdTipoProcedimentoUfficioIdUfficio
      >
    >
  >;

export type DeleteTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioMutationError =
  ErrorType<string>;

/**
 * @summary Cancellazione associazione tra tipo procedimento e ufficio
 */
export const useDeleteTipiProcedimentoIdTipoProcedimentoUfficioIdUfficio = <
  TError = ErrorType<string>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<
        typeof deleteTipiProcedimentoIdTipoProcedimentoUfficioIdUfficio
      >
    >,
    TError,
    { idTipoProcedimento: string; idUfficio: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteTipiProcedimentoIdTipoProcedimentoUfficioIdUfficioMutationOptions(
      options,
    );

  return useMutation(mutationOptions);
};
/**
 * @summary chiamata BtB. Restituisce la lista di id dei tipiProcedimento visibili da quelle competenze della AOO selezionata
 */
export const getTipiProcedimentoAoo = (
  params: GetTipiProcedimentoAooParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-cfg-procedimenti/tipi-procedimento/Aoo`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetTipiProcedimentoAooQueryKey = (
  params: GetTipiProcedimentoAooParams,
) =>
  [
    `/framework-cfg-procedimenti/tipi-procedimento/Aoo`,
    ...(params ? [params] : []),
  ] as const;

export const getGetTipiProcedimentoAooQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiProcedimentoAoo>>,
  TError = ErrorType<string>,
>(
  params: GetTipiProcedimentoAooParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiProcedimentoAoo>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiProcedimentoAoo>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTipiProcedimentoAooQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiProcedimentoAoo>>
  > = ({ signal }) => getTipiProcedimentoAoo(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetTipiProcedimentoAooQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiProcedimentoAoo>>
>;
export type GetTipiProcedimentoAooQueryError = ErrorType<string>;

/**
 * @summary chiamata BtB. Restituisce la lista di id dei tipiProcedimento visibili da quelle competenze della AOO selezionata
 */
export const useGetTipiProcedimentoAoo = <
  TData = Awaited<ReturnType<typeof getTipiProcedimentoAoo>>,
  TError = ErrorType<string>,
>(
  params: GetTipiProcedimentoAooParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiProcedimentoAoo>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiProcedimentoAooQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
